import axios from "axios";
import { HTTP_HEADERS } from "./apiHeader";
import fileDownload from "js-file-download";
import {
  getUnauthorizedRedirectPath,
  isLocalAutoLoginEnabled,
  isLocalDevelopmentHost,
  performLocalAutoLogin,
} from "../../utils/authRedirect";
import {
  BROKER_LOGOUT_RETURN_PATH,
  markBrokerLoggedOut,
} from "../../utils/oktaLogout";
import { SERVICE_URLS } from "./serviceUrls";

const mergeRequestConfig = (baseConfig, overrideConfig = {}) => ({
  ...baseConfig,
  ...overrideConfig,
  headers: {
    ...(baseConfig?.headers || {}),
    ...(overrideConfig?.headers || {}),
  },
});

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const statusCode = error?.response?.status;
    const originalRequest = error?.config;
    const isSessionExpired = statusCode === 401;
    const isLocalHost = isLocalDevelopmentHost();
    const requestUrl =
      typeof originalRequest?.url === "string" ? originalRequest.url : "";
    const isAuthRequest =
      requestUrl.includes(SERVICE_URLS.USER_AUTHENTICATE) ||
      requestUrl.includes(SERVICE_URLS.BROKER_AUTHENTICATE);
    const isLogoutRequest = requestUrl.includes(SERVICE_URLS.USER_LOGOUT);

    // 403 Forbidden: the user is authenticated but is not permitted to access
    // this resource. Do NOT redirect to /login (that would look like a session
    // expiry and mask the real access-denied condition). Reject so the caller
    // can surface the API error message inline.
    if (statusCode === 403) {
      return Promise.reject(error);
    }

    if (isSessionExpired && isLogoutRequest) {
      markBrokerLoggedOut();
      window.location.href = BROKER_LOGOUT_RETURN_PATH;
      return Promise.reject(error);
    }

    if (
      isSessionExpired &&
      isLocalHost &&
      isLocalAutoLoginEnabled() &&
      !isAuthRequest
    ) {
      if (!originalRequest?._localAutoLoginRetry) {
        try {
          originalRequest._localAutoLoginRetry = true;
          await performLocalAutoLogin();
          return axios(originalRequest);
        } catch (autoLoginError) {
          window.location.href = getUnauthorizedRedirectPath();
          return Promise.reject(autoLoginError);
        }
      }
    } else if (isSessionExpired && !isAuthRequest) {
      window.location.href = getUnauthorizedRedirectPath();
    }

    return Promise.reject(error);
  }
);

export const axiosGet = async (url) => {
  return axios.get(url, HTTP_HEADERS().GET);
};

export const axiosPost = async (url, data, requestConfig = {}) => {
  return axios.post(
    url,
    JSON.stringify(data),
    mergeRequestConfig(HTTP_HEADERS().POST, requestConfig)
  );
};

const wait = (delayMs) =>
  new Promise((resolve) => {
    setTimeout(resolve, delayMs);
  });

const isRetryableApiError = (error) => {
  const statusCode = error?.response?.status;

  if (!statusCode) {
    return true;
  }

  return statusCode >= 500;
};

export const axiosPostWithRetry = async (
  url,
  data,
  { maxAttempts = 3, delayMs = 5000 } = {}
) => {
  let lastError;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      return await axiosPost(url, data);
    } catch (error) {
      lastError = error;

      if (!isRetryableApiError(error) || attempt === maxAttempts) {
        throw error;
      }

      await wait(delayMs);
    }
  }

  throw lastError;
};

export const axiosPut = async (url, data) => {
  return axios.put(url, JSON.stringify(data), HTTP_HEADERS().PUT);
};

export const axiosDelete = async (url, data) => {
  var option = HTTP_HEADERS().DELETE;
  option.data = JSON.stringify(data);
  return axios.delete(url, option);
};

export const axiosFormPost = async (url, formData) => {
  return axios.post(url, formData, HTTP_HEADERS("form").POST);
};

export const axiosFormPut = async (url, formData) => {
  return axios.put(url, formData, HTTP_HEADERS("form").PUT);
};

export const axiosDownload = async (url, fileName) => {
  return axios.get(url, HTTP_HEADERS().GET_FILE).then((res) => {
    let comFileName = fileName;
    try {
      let headerLine = res.headers["Content-Disposition"];
      if (headerLine) {
        let startFileNameIndex = headerLine.indexOf('"') + 1;
        let endFileNameIndex = headerLine.lastIndexOf('"');
        comFileName = headerLine.substring(
          startFileNameIndex,
          endFileNameIndex
        );
      }
    } catch (e) {
    }

    fileDownload(res.data, comFileName);
  });
};

export const reactQueryDefaultOptions = {
  staleTime: 1000 * 60 * 5,
  refetchOnWindowFocus: false,
  refetchOnReconnect: true,
  retry: false,
};
